import type { Connection, CreateConnectionRequest, EnvClass } from '@stride/shared'
import type { IConnectionService, RotateSecret } from './IConnectionService.interface'
import { authHeaders } from '../../lib/apiClient'

// ── API base URL ──────────────────────────────────────────────────────────────
const BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001'

// ── Typed fetch helper ────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()), ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}))
    const message = (body as { message?: string }).message ?? `API error ${res.status}`
    throw new Error(message)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ── HttpConnectionService ─────────────────────────────────────────────────────

export class HttpConnectionService implements IConnectionService {
  listConnections(): Promise<Connection[]> {
    return apiFetch<Connection[]>('/api/connections')
  }

  createConnection(req: CreateConnectionRequest): Promise<Connection> {
    return apiFetch<Connection>('/api/connections', {
      method: 'POST',
      body:   JSON.stringify(req),
    })
  }

  deleteConnection(id: string): Promise<void> {
    return apiFetch<void>(`/api/connections/${id}`, { method: 'DELETE' })
  }

  updateConnection(id: string, patch: { deployBranch?: string; label?: string; isArmSource?: boolean; envClass?: EnvClass }): Promise<Connection> {
    return apiFetch<Connection>(`/api/connections/${id}`, {
      method: 'PATCH',
      body:   JSON.stringify(patch),
    })
  }

  rotateCredential(id: string, secret: RotateSecret): Promise<Connection> {
    // POST to a dedicated route rather than PATCH: this carries a secret, and it is the one update that
    // can break every pipeline pointing at the connection, so it should stand alone in an audit log.
    return apiFetch<Connection>(`/api/connections/${id}/credential`, {
      method: 'POST',
      body:   JSON.stringify(secret),
    })
  }

  checkHealth(id: string): Promise<Connection> {
    return apiFetch<Connection>(`/api/connections/${id}/health`, { method: 'POST' })
  }

  getSalesforceOAuthUrl(label: string): string {
    const params = new URLSearchParams({ label })
    return `${BASE}/api/connections/oauth/sf/start?${params.toString()}`
  }

  getGitOAuthUrl(label: string, repoUrl?: string): string {
    const params = new URLSearchParams({ label })
    if (repoUrl !== undefined) params.set('repoUrl', repoUrl)
    return `${BASE}/api/connections/oauth/git/start?${params.toString()}`
  }
}
